<template>
  <div>
    <ComponentHeader title="Task Statuses" subtitle="Define and manage the status states that tasks can have in your workflow">
      <button
        @click="showCreateModal = true"
        class="btn-primary"
      >
        Add Status
      </button>
    </ComponentHeader>

    <div class="bg-white rounded-lg shadow" style="height: 500px">
      <AgGridVue
        :theme="standardListTheme"
        style="width: 100%; height: 100%"
        :column-defs="columnDefs"
        :row-data="rowData"
        :default-col-def="defaultColDef"
        :loading="loading"
        @grid-ready="onGridReady"
      />
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal" class="fixed inset-0 bg-black/50 z-50">
      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h3 class="text-lg font-semibold mb-4">
              {{ editingStatus ? 'Edit' : 'Create' }} Task Status
            </h3>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium mb-1">Name</label>
                <input
                  v-model="formData.name"
                  type="text"
                  class="w-full px-3 py-2 border rounded-md"
                  placeholder="Status name"
                >
              </div>

              <div class="flex items-center">
                <input
                  v-model="formData.completed"
                  type="checkbox"
                  id="completed"
                  class="mr-2"
                >
                <label for="completed" class="text-sm font-medium">
                  This status indicates task completion
                </label>
              </div>
            </div>

            <div class="mt-6 flex justify-end space-x-3">
              <button
                @click="closeModal"
                class="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                @click="saveStatus"
                class="btn-primary"
              >
                {{ editingStatus ? 'Update' : 'Create' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import { standardListTheme } from '@/utils/common';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default {
  components: {
    AgGridVue,
    ComponentHeader
  },
  data() {
    return {
      loading: true,
      rowData: [],
      showCreateModal: false,
      editingStatus: null,
      formData: {
        name: '',
        completed: false
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      systemStatuses: [0, 1, 2, 3, 4, 5, 6] // IDs to protect
    };
  },
  computed: {
    standardListTheme() {
      return standardListTheme;
    },
    columnDefs() {
      return [
        {
          headerName: 'ID',
          field: 'id',
          width: 80
        },
        {
          headerName: 'Name',
          field: 'name',
          flex: 1
        },
        {
          headerName: 'Completed',
          field: 'completed',
          width: 120,
          cellRenderer: params => {
            return params.value
              ? '<span class="text-green-600">✓ Yes</span>'
              : '<span class="text-gray-400">No</span>';
          }
        },
        {
          headerName: 'System',
          width: 100,
          valueGetter: params => this.systemStatuses.includes(params.data.id),
          cellRenderer: params => {
            return params.value
              ? '<span class="text-blue-600">System</span>'
              : '';
          }
        },
        {
          headerName: 'Actions',
          width: 150,
          cellRenderer: params => {
            const isSystem = this.systemStatuses.includes(params.data.id);
            if (isSystem) {
              return '<span class="text-gray-400">Protected</span>';
            }
            return `
              <div class="flex space-x-2">
                <button class="edit-btn text-blue-600 hover:text-blue-800">Edit</button>
                <button class="delete-btn text-red-600 hover:text-red-800">Delete</button>
              </div>
            `;
          },
          onCellClicked: (params) => {
            if (this.systemStatuses.includes(params.data.id)) return;

            if (params.event.target.classList.contains('edit-btn')) {
              this.editStatus(params.data);
            } else if (params.event.target.classList.contains('delete-btn')) {
              this.deleteStatus(params.data);
            }
          }
        }
      ];
    }
  },
  methods: {
    async fetchStatuses() {
      try {
        const response = await axios.get('/api/taskstatuses/');
        this.rowData = response.data;
        this.loading = false;
      } catch (error) {
        console.error('Error fetching task statuses:', error);
        this.loading = false;
      }
    },

    async saveStatus() {
      try {
        if (this.editingStatus) {
          await axios.patch(`/api/taskstatuses/${this.editingStatus.id}/`, this.formData);
        } else {
          await axios.post('/api/taskstatuses/', this.formData);
        }
        this.closeModal();
        this.fetchStatuses();
      } catch (error) {
        console.error('Error saving status:', error);
      }
    },

    editStatus(status) {
      this.editingStatus = status;
      this.formData = {
        name: status.name,
        completed: status.completed
      };
      this.showCreateModal = true;
    },

    async deleteStatus(status) {
      if (confirm(`Delete status "${status.name}"?`)) {
        try {
          await axios.delete(`/api/taskstatuses/${status.id}/`);
          this.fetchStatuses();
        } catch (error) {
          console.error('Error deleting status:', error);
          alert('Cannot delete status - it may be in use');
        }
      }
    },

    closeModal() {
      this.showCreateModal = false;
      this.editingStatus = null;
      this.formData = {
        name: '',
        completed: false
      };
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.fetchStatuses();
    }
  },
  mounted() {
    this.fetchStatuses();
  }
};
</script>
